import React, { useCallback, useState, useMemo } from 'react';
import './DataSatisfaction.scss';
import { CircularProgress, Popover, Stack, Typography } from '@mui/material';
import {
  dataTrendPeriodAtom,
  selectedDateAtom,
  useAuthContext,
  useSatisfactionEmas,
  useUpdateSatisfactionEma,
} from '../../../../data-access';
import dayjs from 'dayjs';
import { useRecoilValue } from 'recoil';
import {
  getSatisfactionIcon,
  satisfactionColor,
} from '../../../DataView/constants';
import { EMASatisfactionType } from '@demind-inc/core';
import { trackEventMixpanel } from '../../../../utils';
import { debounce } from 'lodash';
import { AnswerSatisfactionPopup } from './AnswerSatisfactionPopup';
import { ViewAllContainer } from '../ViewAllContainer';
import { SatisfactionDataFullView } from './SatisfactionDataFullView';
import { ViewAllTitle } from '../../types';
import { useTranslation } from 'react-i18next';
import { getSatisfactionText, getWeekdayLabels } from '../../i18n';

const DataSatisfaction: React.FC<{ isSimplified?: boolean }> = ({ isSimplified = false }) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const dataTrendPeriod = useRecoilValue(dataTrendPeriodAtom);
  const isWeekly = dataTrendPeriod === 'weekly';
  const weekdayLabels = useMemo(() => getWeekdayLabels(t, 'narrow'), [t]);
  const [viewAllTitle, setViewAllTitle] = useState<ViewAllTitle | null>(null);
  const { satisfactionEmas, isLoading } = useSatisfactionEmas({
    userId: user?.userId,
    startDate: dayjs(selectedDate).startOf('week').format('YYYY-MM-DD'),
    endDate: dayjs(selectedDate).endOf('week').format('YYYY-MM-DD'),
  });
  const { mutate: updateSatisfactionEma } = useUpdateSatisfactionEma();
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const getSatisfactionForDate = useCallback(
    (date: string): EMASatisfactionType | null => {
      return satisfactionEmas.find((day) => day.date === date)?.satisfaction?.value || null;
    },
    [satisfactionEmas]
  );

  const currentDateSatisfaction = useMemo(() => {
    const currentDateFormatted = dayjs(selectedDate).format('YYYY-MM-DD');
    const satisfaction = getSatisfactionForDate(currentDateFormatted);
    return {
      value: satisfaction,
      isAnswered: !!satisfaction,
      date: currentDateFormatted,
    };
  }, [selectedDate, getSatisfactionForDate]);

  const renderSatisfactionIcon = useCallback(
    (satisfaction: EMASatisfactionType | null, isAnswered: boolean, size: number = 33) => {
      return getSatisfactionIcon(
        satisfaction || 'unanswered',
        isAnswered ? 'selected' : 'unselected',
        size
      );
    },
    []
  );

  const getPopupDefaultValue = useCallback(() => {
    if (isWeekly && selectedDay) {
      return getSatisfactionForDate(selectedDay);
    }
    return currentDateSatisfaction.value;
  }, [isWeekly, selectedDay, getSatisfactionForDate, currentDateSatisfaction.value]);

  const debouncedUpdateSatisfactionEma = useCallback(
    debounce((date: string, value: EMASatisfactionType) => {
      if (!user?.userId) {
        return;
      }
      updateSatisfactionEma({
        userId: user?.userId,
        date,
        satisfaction: value,
      });
      trackEventMixpanel('satisfaction_answer', {
        satisfaction: value,
        from: 'data_screen',
      });
    }, 2000),
    [selectedDate, user?.userId]
  );

  const handleEmaClick = (
    date: string,
    currentValue: EMASatisfactionType | null,
    event: React.MouseEvent<HTMLElement>
  ) => {
    setSelectedDay(date);
    setAnchorEl(event.currentTarget);
  };

  const handleSatisfactionSelect = (value: EMASatisfactionType) => {
    setAnchorEl(null);
    if (selectedDay && isWeekly) {
      debouncedUpdateSatisfactionEma(selectedDay, value);
      setSelectedDay(null);
      return;
    }
    debouncedUpdateSatisfactionEma(currentDateSatisfaction.date, value);
  };

  const handlePopupClose = () => {
    setAnchorEl(null);
    if (isWeekly) {
      setSelectedDay(null);
    }
  };

  if (!isWeekly) {
    return (
      <Stack className="data-satisfaction">
        <ViewAllContainer title={viewAllTitle} onClose={() => setViewAllTitle(null)}>
          <SatisfactionDataFullView />
        </ViewAllContainer>
        <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body1" className="data-satisfaction__title">
            {t('data.satisfaction.title')}
          </Typography>
          <Typography
            variant="subtitle2"
            className="data-satisfaction__view-all"
            onClick={() => setViewAllTitle(t('data.satisfaction.title'))}
          >
            {t('data.common.viewAll')}
          </Typography>
        </Stack>

        <div className="data-satisfaction__value-container">
          {renderSatisfactionIcon(
            currentDateSatisfaction.value,
            currentDateSatisfaction.isAnswered
          )}
          <Typography
            variant="h3"
            className="data-satisfaction__value-container__text"
            color={satisfactionColor[currentDateSatisfaction.value || 'unanswered']}
          >
            {getSatisfactionText(t, currentDateSatisfaction.value || 'unanswered')}
          </Typography>
        </div>

        <AnswerSatisfactionPopup
          defaultValue={getPopupDefaultValue()}
          onSelect={handleSatisfactionSelect}
        />
      </Stack>
    );
  }

  if (isSimplified) {
    return (
      <Stack className="data-satisfaction-simplified" direction="row" gap={0.5}>
        {satisfactionEmas.map((day, index) => {
          const isSelected = day.date === selectedDay;
          const daysSatisfaction = day.satisfaction?.value || null;
          const isAnswered = !!daysSatisfaction;

          return (
            <div key={index} className="data-satisfaction__day-container">
              <Typography
                variant="caption"
                className={`data-satisfaction__day-text${isSelected ? ' selected' : ''}`}
              >
                {weekdayLabels[dayjs(day.date).day()]}
              </Typography>

              {!isAnswered ? (
                <div className="data-satisfaction__day-container__not-answered" />
              ) : (
                renderSatisfactionIcon(daysSatisfaction, isAnswered, 20)
              )}
            </div>
          );
        })}
      </Stack>
    );
  }
  return (
    <div className="data-satisfaction">
      <ViewAllContainer title={viewAllTitle} onClose={() => setViewAllTitle(null)}>
        <SatisfactionDataFullView />
      </ViewAllContainer>
      <Stack mb={2} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body1" className="data-satisfaction__title">
          {t('data.satisfaction.title')}
        </Typography>
        <Typography
          variant="subtitle2"
          className="data-satisfaction__view-all"
          onClick={() => setViewAllTitle(t('data.satisfaction.title'))}
        >
          {t('data.common.viewAll')}
        </Typography>
      </Stack>
      {isLoading ? (
        <Stack justifyContent="center" alignItems="center" flex={1} height={85}>
          <CircularProgress size={32} />
        </Stack>
      ) : (
        <div className="data-satisfaction__week-container">
          {satisfactionEmas.map((day, index) => {
            const isSelected = day.date === selectedDay;
            const daysSatisfaction = day.satisfaction?.value || null;
            const isAnswered = !!daysSatisfaction;
            const isFutureDate = dayjs(day.date).isAfter(dayjs());
            return (
              <div key={index} className="data-satisfaction__day-container">
                <Typography
                  variant="body2"
                  className={`data-satisfaction__day-text${isSelected ? ' selected' : ''}`}
                >
                  {weekdayLabels[dayjs(day.date).day()]}
                </Typography>
                <button
                  className="data-satisfaction__ema-button"
                  onClick={(e) => handleEmaClick(day.date, daysSatisfaction, e)}
                  disabled={isFutureDate}
                >
                  {!isAnswered ? (
                    <div className="data-satisfaction__day-container__not-answered" />
                  ) : (
                    renderSatisfactionIcon(daysSatisfaction, isAnswered, 20)
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handlePopupClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          style: {
            borderRadius: '16px',
          },
        }}
      >
        <AnswerSatisfactionPopup
          defaultValue={getPopupDefaultValue()}
          onSelect={handleSatisfactionSelect}
        />
      </Popover>
    </div>
  );
};

export default DataSatisfaction;
